import { dataVizKeyOf, isDataVizToneName } from "../../theme/dataVizPalette";
import type { ColorTokens } from "../../theme/tokens";
import type { ThemeMode } from "../../theme/ThemeProvider";
import type { InteractiveSurfaceSpec } from "../foundations/interactiveSurface";
import type { IconButtonTone, IconButtonVariant } from "./IconButton";

export const cssColorMix = (base: string, percent: number, anchor: string) =>
  `color-mix(in srgb, ${base} ${percent}%, ${anchor})`;

const getTone = (
  color: ColorTokens,
  tone: IconButtonTone,
  mode: ThemeMode,
  mix: typeof cssColorMix,
) => {
  if (isDataVizToneName(tone)) {
    const key = dataVizKeyOf(tone);
    const base = color.dataViz[key];
    // Amber의 높은 휘도 때문에 light hover도 본문 대비 4.5:1을 지키도록 혼합량을 제한한다.
    const rest = mode === "dark" ? 18 : key === "amber" ? 54 : 60;
    const hover = mode === "dark" ? 26 : key === "amber" ? 60 : key === "green" ? 65 : 66;
    const active = mode === "dark" ? 34 : key === "amber" ? 48 : 54;
    const anchor = mode === "dark" ? color.bg.surface : color.text.default;
    const inkWeight = mode === "dark" ? 50 : 40;
    const ink = mix(base, inkWeight, color.text.default);
    return {
      text: ink,
      border: ink,
      hover: mix(base, inkWeight - 10, color.text.default),
      active: mix(base, inkWeight - 20, color.text.default),
      solidForeground: mode === "dark" ? color.text.default : color.text.inverse,
      solidRest: mix(base, rest, anchor),
      solidHover: mix(base, hover, anchor),
      solidActive: mix(base, active, anchor),
    };
  }
  switch (tone) {
    case "secondary":
      return {
        text: color.text.default,
        border: color.border.selected,
        hover: color.border.selectedHover,
        active: color.border.selectedActive,
        solidForeground: color.text.default,
        solidRest: color.bg.control.secondary,
        solidHover: color.bg.control.secondaryHover,
        solidActive: color.bg.control.secondaryActive,
      };
    case "neutral":
      return {
        text: color.text.default,
        border: color.border.default,
        hover: color.border.strong,
        active: color.border.emphasis,
        solidForeground: color.text.default,
        solidRest: color.bg.control.neutral,
        solidHover: color.bg.control.neutralHover,
        solidActive: color.bg.control.neutralActive,
      };
    case "danger":
      return {
        text: color.text.danger,
        border: color.border.danger,
        hover: color.bg.control.dangerHover,
        active: color.bg.control.dangerActive,
        solidForeground: color.text.onDangerControl,
        solidRest: color.bg.control.danger,
        solidHover: color.bg.control.dangerHover,
        solidActive: color.bg.control.dangerActive,
      };
    case "warning":
      return {
        text: color.text.warning,
        // warning은 border.* 축이 없어 outline도 text.warning(orange)을 쓴다 — 모든 면 위
        // 4.55:1 이상이라 1.4.11 3:1 하한을 넘는다.
        border: color.text.warning,
        hover: color.bg.control.warningHover,
        active: color.bg.control.warningActive,
        solidForeground: color.text.onWarningControl,
        solidRest: color.bg.control.warning,
        solidHover: color.bg.control.warningHover,
        solidActive: color.bg.control.warningActive,
      };
    case "primary":
      return {
        text: color.text.accent,
        border: color.border.selected,
        hover: color.border.selectedHover,
        active: color.border.selectedActive,
        solidForeground: color.text.onControl,
        solidRest: color.bg.control.primary,
        solidHover: color.bg.control.primaryHover,
        solidActive: color.bg.control.primaryActive,
      };
  }
};

/**
 * IconButton 팔레트 → 공유 `InteractiveSurfaceSpec`. tone × variant × selected 조합을
 * 상태별 슬롯에 매핑하고, 셀렉터 구조는 interactiveSurfaceCss 와 공유한다.
 *
 * - bg: solid 는 tone fill(rest/hover/active) 진행, ghost/surface 는 surface 사다리 +2/+3 rung 점프.
 * - 전경(아이콘색): solid primary/warning/danger는 onControl 고정, tonal secondary/neutral은
 *   text.default 고정. 그 외엔 primary 비선택만 rest neutral → hover/active accent다.
 *   secondary/neutral의 ghost/surface 조합은 전 상태 중립 전경을 유지한다.
 * - 테두리: `$border=false`면 transparent. solid 는 rest 색 유지(hover/active 에서 안 바뀜),
 *   bordered ghost 는 tone 축으로 진행한다. surface 비선택은 neutral outline을 유지하고,
 *   selected만 tone 축을 사용해 같은 hover 위치에서 선택 해제가 즉시 읽히게 한다.
 *   neutral selected surface는 경계가 유일한 지속 cue라 strong → emphasis로 한 단계 강화한다.
 * - selected(토글)는 슬롯이 아니라 입력 — 선택 상태에 맞는 테두리/전경 진행을 고른다.
 *   solid는 fill 자체가 tone을 소유해 outline만으로 지속 상태를 보장하지 않으므로, 같은 glyph를
 *   유지해야 하는 토글은 surface를 쓰고 solid 토글은 선택 시 glyph/accessible name을 바꾼다.
 */
export function buildIconButtonSurface(
  color: ColorTokens,
  variant: IconButtonVariant,
  tone: IconButtonTone,
  selected: boolean,
  border: boolean,
  mode: ThemeMode = "light",
  mix: typeof cssColorMix = cssColorMix,
): InteractiveSurfaceSpec {
  const t = getTone(color, tone, mode, mix);
  const isSolid = variant === "solid";
  const isSelectedSurface = variant === "surface" && selected;
  const restBackground = isSolid
    ? t.solidRest
    : variant === "surface"
      ? color.bg.surfaceSunken
      : "transparent";
  const hoverBackground = isSolid
    ? t.solidHover
    : variant === "surface"
      ? color.bg.surfaceRaisedHigh
      : color.bg.surfaceRaised;
  const activeBackground = isSolid
    ? t.solidActive
    : variant === "surface"
      ? color.bg.surfaceOverlay
      : color.bg.surfaceRaisedHigh;

  const restForeground = isSolid
    ? t.solidForeground
    : selected || tone !== "primary"
      ? t.text
      : color.text.default;
  const interactForeground = isSolid ? t.solidForeground : t.text;

  // Solid는 fill이 tone을 이미 소유하므로 기존 tone outline을 유지한다. 모든 tone/theme에서
  // 통하는 별도 selected-on-solid 색은 만들지 않고, 지속 상태는 glyph 변경으로 전달한다.
  // Surface 비선택은 배경 사다리에 묻히지 않는 neutral border ramp를 쓰고, selected는 tone
  // border를 쓰되 neutral은 지속 cue를 한 단계 강화한다.
  const selectedRestBorder = isSelectedSurface && tone === "neutral" ? t.hover : t.border;
  const selectedHoverBorder = isSelectedSurface && tone === "neutral" ? t.active : t.hover;
  const restBorder = !border ? "transparent" : selected ? selectedRestBorder : color.border.default;
  const hoverBorder = !border
    ? "transparent"
    : isSolid
      ? restBorder
      : selected
        ? selectedHoverBorder
        : variant === "surface"
          ? color.border.strong
          : t.border;
  const activeBorder = !border
    ? "transparent"
    : isSolid
      ? restBorder
      : selected
        ? t.active
        : variant === "surface"
          ? color.border.emphasis
          : t.hover;

  return {
    rest: { background: restBackground, border: restBorder, foreground: restForeground },
    hover: { background: hoverBackground, border: hoverBorder, foreground: interactForeground },
    active: { background: activeBackground, border: activeBorder, foreground: interactForeground },
    // disabled는 interaction tone을 제거한다. ghost/surface 비선택은 disabledSunken으로
    // recede하고, solid는 채워진 variant의 shape가 사라지지 않도록 control.disabled 면을
    // 유지한다. selected도 같은 neutral 면을 쓰되 기본 활성화된 surfaceOverlay outline으로
    // toggle 값을 보존한다. explicit border=false는 transparent를 유지한다.
    disabled: {
      background: selected || isSolid ? color.bg.control.disabled : color.bg.control.disabledSunken,
      border: border ? color.bg.surfaceOverlay : "transparent",
      foreground: color.text.disabled,
    },
  };
}
